import { IQuery } from 'core-ts-types';
import * as _4 from 'lodash4';
import { Query } from 'utility-ts-orm';
import { SearchAndCourseFeatureFacade } from '../../../../search_and_add_courses/facades/search_and_courses_feature_facade';
import { SchedulerAvailableTermsQuery } from '../../../../search_and_add_courses/queries/scheduler_available_terms_query';
import { IApplicationConfiguration } from '../../../../interfaces/scheduler_interfaces';
import { RegistrationPinsPermissionQuery } from '../../../registration/queries/registration_pins_permission_query';
import { TermPinServant } from '../../../../components/term_pin/servants/term_pin_servant';
import { PayTuitionQuery } from '../../../registration/queries/pay_tuition_query';
import { RegistrationOneClickQuery } from '../../../registration/queries/registration_one_click_query';
import { CustomContentsServant } from 'data-ng-custom-text';
import { IRegisterSectionsDatasource } from '../../../course_scheduler/datasources/register_sections_datasource';
import { AppointmentScheduleModel } from '../models/appointment_schedule_model';
import { RegistrationOneClickModel } from '../../../registration/models/registration_one_click_model';
import { ISchedulerAvailableTermDatasource } from '../../../../scheduler_active_term/datasource/scheduler_available_terms_datasource';
import { ScreenDeviceModel } from 'screen-query';
import { ScheduledSectionTileServant } from '../../../course_scheduler/servants/scheduled_section_tile_servant';
import { SelectedSectionActionsFacade } from '../../../course_scheduler/facades/selected_section_actions_facade';
import { SectionSelectionsQuery } from '../../../../components/section_selections/queries/section_selections_query';
import { SectionSelectionsModel } from '../../../../components/section_selections/models/section_selections_model';
import { RequestStateModel } from '../../../../request_state/models/request_state_model';
import { RequestStateQuery } from '../../../../request_state/queries/request_state_query';

export interface ISchedulerNavMenuItem {
  value: string;
  title: string;
  visible: boolean;
  external_link_icon: boolean;
}

export interface ISchedulerNavSubmenuItem {
  title: string;
  visible: boolean;
  children: ISchedulerNavMenuItem[];
}

interface ISchedulerNavigationMenuQueryResult {
  submenuItems: ISchedulerNavSubmenuItem[];
  menuItems: ISchedulerNavMenuItem[];
}

export class SchedulerNavigationMenuQuery implements IQuery {
  private _result: ISchedulerNavigationMenuQueryResult;

  constructor(
    private ngSearchAndCourseFeatureFacade: SearchAndCourseFeatureFacade,
    private ngSchedulerAvailableTermsQuery: SchedulerAvailableTermsQuery,
    private appConfigModel: IApplicationConfiguration,
    private ngRegistrationPinsPermissionQuery: RegistrationPinsPermissionQuery,
    private ngTermPinServant: TermPinServant,
    private ngPayTuitionQuery: PayTuitionQuery,
    private ngRegistrationOneClickModel: RegistrationOneClickModel,
    private ngRegistrationOneClickQuery: RegistrationOneClickQuery,
    private ngCustomContentsServant: CustomContentsServant,
    private ngRegisterSectionsDatasource: IRegisterSectionsDatasource,
    private ngAppointmentScheduleModel: AppointmentScheduleModel,
    private ngSchedulerAvailableTermDatasource: ISchedulerAvailableTermDatasource,
    private ngScreenDeviceModel: ScreenDeviceModel,
    private ngSectionSelectionsModel: SectionSelectionsModel,
    private ngSectionSelectionsQuery: SectionSelectionsQuery,
    private ngScheduledSectionTileServant: ScheduledSectionTileServant,
    private ngSelectedSectionActionsFacade: SelectedSectionActionsFacade
  ) {
    Query.registerQuery(
      this,
      ngRegistrationOneClickModel,
      ngSchedulerAvailableTermDatasource,
      ngScreenDeviceModel,
      ngSectionSelectionsModel
    );
  }

  getSubmenuItems() {
    const submenuItems: ISchedulerNavSubmenuItem[] = [
      {
        title: 'Switch Terms',
        visible: this.ngSearchAndCourseFeatureFacade.isFeatureEnabled,
        children: this.ngSchedulerAvailableTermsQuery.result.terms.map((term) => ({
          value: `switchTerm-${term.id}`,
          title: term.name,
          visible: true,
          external_link_icon: false
        }))
      }
    ].filter((item) => item.visible);
    return submenuItems;
  }

  getMenuItems() {
    const isScreenDeviceDesktop = _4.isEqual(
      _4.get(this.ngScreenDeviceModel.read(), 'screen_device', 'desktop'),
      'desktop'
    );
    const menuItems: ISchedulerNavMenuItem[] = [
      {
        value: 'myPlannedCourses',
        title: 'My Planned Courses',
        visible: true,
        external_link_icon: false
      },
      {
        value: 'searchForSections',
        title: 'Search for Sections',
        visible: true,
        external_link_icon: false
      },
      {
        value: 'searchForCourses',
        title: 'Search for Courses',
        visible: this.ngSearchAndCourseFeatureFacade.isFeatureEnabled,
        external_link_icon: false
      },
      {
        value: 'transactionHistory',
        title: 'Transaction History',
        visible: true,
        external_link_icon: false
      },
      {
        value: 'scheduleAppointmentDesktop',
        title: this.ngAppointmentScheduleModel.read().label,
        visible: this.appConfigModel.isFeatureEnabled('ssccampus-appointment_scheduling') && isScreenDeviceDesktop,
        external_link_icon: false
      },
      {
        value: 'scheduleAppointmentMobile',
        title: this.ngAppointmentScheduleModel.read().label,
        visible: this.appConfigModel.isFeatureEnabled('ssccampus-appointment_scheduling') && !isScreenDeviceDesktop,
        external_link_icon: false
      },
      {
        value: 'quickSchedule',
        title: 'Quick Scheduler',
        visible: this.appConfigModel.isFeatureEnabled('reg-quick_scheduler'),
        external_link_icon: false
      },
      {
        value: 'preferences',
        title: 'Preferences',
        visible: true,
        external_link_icon: false
      },
      {
        value: 'sectionPin',
        title: `Enter ${this.ngRegistrationPinsPermissionQuery.result.sectionPinText}(s)`,
        visible: this.appConfigModel.model.member_features['reg-class_permission'],
        external_link_icon: false
      },
      {
        value: 'termPin',
        title: 'Enter Term PIN',
        visible: this.ngTermPinServant.isTermPinFeatureEnabled(),
        external_link_icon: false
      },
      {
        value: 'payTuition',
        title: 'Pay Tuition',
        visible: this.ngPayTuitionQuery.result.showPayTuition,
        external_link_icon: true
      },
      {
        value: 'registrationPortal',
        title: 'Registration Portal',
        visible: !this.ngRegistrationOneClickQuery.result.isOneClickRegistrationEnabled,
        external_link_icon: true
      },
      {
        value: 'registerSections',
        title: `Register ${this.ngRegistrationOneClickQuery.result.selectedSectionsText}`,
        visible:
          this.ngRegistrationOneClickQuery.result.isOneClickRegistrationEnabled &&
          this.ngRegistrationOneClickQuery.result.showRegisterButton,
        external_link_icon: false
      },
      {
        value: 'dropSections',
        title: `Drop (${this.ngSectionSelectionsQuery.result.dropSelections.length})`,
        visible: this.ngSelectedSectionActionsFacade.isMultiDropFeatureEnabled(),
        external_link_icon: false
      },
      {
        value: 'withdrawSections',
        title: `Withdraw (${this.ngSectionSelectionsQuery.result.withdrawSelections.length})`,
        visible:
          this.ngSelectedSectionActionsFacade.isMultiDropFeatureEnabled() &&
          this.ngScheduledSectionTileServant.isWithdrawEnabled(),
        external_link_icon: false
      },
      {
        value: 'waitlistSections',
        title: `Waitlist (${this.ngSectionSelectionsQuery.result.waitlistSelections.length})`,
        visible: this.ngSelectedSectionActionsFacade.shouldShowMultiWaitlistFeature(),
        external_link_icon: false
      }
    ].filter((item) => item.visible);
    return menuItems;
  }

  run() {
    this._result = {
      menuItems: this.getMenuItems(),
      submenuItems: this.getSubmenuItems()
    };
    return this;
  }

  get result() {
    return this._result;
  }
}

angular.module('capability-ng-scheduler').service('ngSchedulerNavigationMenuQuery', SchedulerNavigationMenuQuery);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/aside/scheduler_navigation/queries/scheduler_navigation_menu_query.ts